/**
 * Demo / client-side stand-in for the org workbook field registry.
 * Stable field_id is org-scoped; workbooks place fields, they do not redefine them.
 */

import {
    PINNED_WORKBOOK_FIELD_IDS,
    WORKBOOK_FIELD_CATALOG,
} from '../ApplicationWorkbook/workbookCatalog'
import {
    GENDER_FIELD_ID,
    genderQuestionSpec,
} from '../ApplicationWorkbook/genderQuestion'
import type { CustomFieldInputType } from '../ApplicationWorkbook/workbook.types'

export type OrgFieldSource = 'ezos_catalog' | 'org_custom'

export interface OrgWorkbookField {
    field_id: string
    question_text: string
    input_type: string
    options?: string[]
    source: OrgFieldSource
    pinned?: boolean
    /** Demo: where this field already appears (workbook / section labels). */
    usedIn?: string[]
}

export interface FieldIdCollision {
    field_id: string
    existing: OrgWorkbookField
}

const STOP_WORDS = new Set([
    'a',
    'an',
    'the',
    'to',
    'of',
    'for',
    'and',
    'or',
    'is',
    'are',
    'do',
    'does',
    'you',
    'your',
    'any',
    'all',
    'with',
    'from',
    'in',
    'on',
    'at',
    'be',
    'have',
    'has',
    'please',
    'list',
    'enter',
    'provide',
])

function seedFromCatalog(): Map<string, OrgWorkbookField> {
    const map = new Map<string, OrgWorkbookField>()
    for (const entry of WORKBOOK_FIELD_CATALOG) {
        map.set(entry.id, {
            field_id: entry.id,
            question_text: entry.label,
            input_type: entry.input_type,
            source: 'ezos_catalog',
            pinned: PINNED_WORKBOOK_FIELD_IDS.has(entry.id),
            usedIn: ['EZOS catalog'],
        })
    }
    // Demo org custom — collision UX can be exercised immediately.
    map.set('allergies', {
        field_id: 'allergies',
        question_text: 'List all known allergies',
        input_type: 'textarea',
        source: 'org_custom',
        usedIn: ['Org Standard Workbook · Operational'],
    })
    return map
}

/** Session-level demo registry (module singleton). */
let registry: Map<string, OrgWorkbookField> = seedFromCatalog()

export function resetOrgFieldRegistryForTests(): void {
    registry = seedFromCatalog()
}

export function listOrgFields(): OrgWorkbookField[] {
    return Array.from(registry.values())
}

export function getOrgField(fieldId: string): OrgWorkbookField | undefined {
    return registry.get(fieldId)
}

export function slugifyFieldId(questionText: string): string {
    const tokens = questionText
        .toLowerCase()
        .replace(/[^a-z0-9\s_]/g, ' ')
        .split(/\s+/)
        .filter((t) => t && !STOP_WORDS.has(t))
    let base = tokens.slice(0, 6).join('_')
    if (!base) {
        base = 'custom_field'
    }
    if (base.length > 48) {
        base = base.slice(0, 48).replace(/_+$/, '')
    }
    return base
}

/** Suggest a unique field_id from question text (may still collide if base taken). */
export function suggestFieldId(questionText: string): string {
    return slugifyFieldId(questionText)
}

/**
 * If `preferred` is taken, append a disambiguator from the new question
 * (e.g. allergies + "allergic to pets" → allergies_pets).
 */
export function suggestAlternativeFieldId(
    preferred: string,
    newQuestionText: string,
): string {
    const extra = slugifyFieldId(newQuestionText)
        .split('_')
        .filter((t) => t && !preferred.split('_').includes(t))
        .slice(0, 3)
        .join('_')
    let candidate = extra ? `${preferred}_${extra}` : `${preferred}_2`
    if (candidate.length > 56) {
        candidate = candidate.slice(0, 56).replace(/_+$/, '')
    }
    let n = 2
    while (registry.has(candidate)) {
        const suffix = `_${n}`
        candidate = `${(extra ? `${preferred}_${extra}` : preferred).slice(
            0,
            56 - suffix.length,
        )}${suffix}`
        n += 1
    }
    return candidate
}

/** Blur / pre-add check — stand-in for GET .../workbook-fields/check/?field_id= */
export function checkFieldIdCollision(
    fieldId: string,
): FieldIdCollision | null {
    const id = fieldId.trim()
    if (!id) return null
    const existing = registry.get(id)
    if (!existing) return null
    return { field_id: id, existing }
}

export function ensureUniqueFieldId(preferred: string): string {
    if (!registry.has(preferred)) return preferred
    let n = 2
    let candidate = `${preferred}_${n}`
    while (registry.has(candidate)) {
        n += 1
        candidate = `${preferred}_${n}`
    }
    return candidate
}

export function registerOrgCustomField(input: {
    field_id: string
    question_text: string
    input_type: CustomFieldInputType | string
    options?: string[]
    usedIn?: string[]
}): OrgWorkbookField {
    const existing = registry.get(input.field_id)
    if (existing) {
        return existing
    }
    const field: OrgWorkbookField = {
        field_id: input.field_id,
        question_text: input.question_text.trim() || 'Custom question',
        input_type: input.input_type,
        options: input.options,
        source: 'org_custom',
        usedIn: input.usedIn ?? ['This workbook'],
    }
    registry.set(field.field_id, field)
    return field
}

export function orgFieldToSectionDraft(
    field: OrgWorkbookField,
    opts?: { enabled?: boolean; required?: boolean },
): {
    field_id: string
    question_text: string
    input_type: string
    enabled: boolean
    required: boolean
    is_custom: boolean
    pinned?: boolean
    options?: string[]
    gender_question_mode?: 'id_marker' | 'self_id'
} {
    const gender =
        field.field_id === GENDER_FIELD_ID
            ? genderQuestionSpec('id_marker')
            : null
    return {
        field_id: field.field_id,
        question_text: gender?.question_text || field.question_text,
        input_type: field.input_type,
        enabled: opts?.enabled ?? true,
        required: opts?.required ?? field.field_id === GENDER_FIELD_ID,
        is_custom: field.source === 'org_custom',
        pinned: Boolean(field.pinned),
        ...(field.options || gender
            ? { options: [...(gender?.options || field.options || [])] }
            : {}),
        ...(gender
            ? { gender_question_mode: gender.gender_question_mode }
            : {}),
    }
}

/** Pluggable registry for Configure Section (org demo or EZOS API-backed). */
export interface FieldRegistryAdapter {
    listFields: () => OrgWorkbookField[]
    checkCollision: (fieldId: string) => FieldIdCollision | null
    suggestFieldId: (questionText: string) => string
    suggestAlternativeFieldId: (
        preferred: string,
        newQuestionText: string,
    ) => string
    ensureUniqueFieldId: (preferred: string) => string
    registerField: (input: {
        field_id: string
        question_text: string
        input_type: CustomFieldInputType | string
        options?: string[]
        usedIn?: string[]
        default_section_id?: string
    }) => OrgWorkbookField | Promise<OrgWorkbookField>
}

export const defaultOrgFieldRegistryAdapter: FieldRegistryAdapter = {
    listFields: listOrgFields,
    checkCollision: checkFieldIdCollision,
    suggestFieldId,
    suggestAlternativeFieldId,
    ensureUniqueFieldId,
    registerField: registerOrgCustomField,
}
